import * as React from 'react';
import type {
	IGroupedOption,
	IOption,
	ISelectGroupRenderModel,
	IUseSelectGroupsProps,
	IUseSelectGroupsResult,
	SelectOptionValue,
} from '../types';

const getGroupValue = <TValue extends SelectOptionValue, TMetadata>(
	group: IGroupedOption<TValue, TMetadata>,
	index: number
): string => group.value ?? `${group.label}-${String(index)}`;

const getNormalizedGroupOptions = <TValue extends SelectOptionValue, TMetadata>(
	group: IGroupedOption<TValue, TMetadata>
): ReadonlyArray<IOption<TValue, TMetadata>> => {
	if (!group.disabled) {
		return group.options;
	}

	return group.options.map(option => ({ ...option, disabled: true }));
};

const getEnabledOptions = <TValue extends SelectOptionValue, TMetadata>(
	options: ReadonlyArray<IOption<TValue, TMetadata>>
): ReadonlyArray<IOption<TValue, TMetadata>> => options.filter(option => !option.disabled);

const getSelectedEnabledValues = <TValue extends SelectOptionValue, TMetadata>(
	options: ReadonlyArray<IOption<TValue, TMetadata>>,
	selectedValues: ReadonlySet<TValue>
): ReadonlyArray<TValue> =>
	getEnabledOptions(options)
		.filter(option => selectedValues.has(option.value))
		.map(option => option.value);

const getGroupRenderModel = <TValue extends SelectOptionValue, TMetadata>({
	group,
	index,
	selectedValues,
}: {
	group: IGroupedOption<TValue, TMetadata>;
	index: number;
	selectedValues: ReadonlySet<TValue>;
}): ISelectGroupRenderModel<TValue, TMetadata> => {
	const options = getNormalizedGroupOptions(group);
	const enabledOptions = getEnabledOptions(options);
	const selectedEnabledValues = getSelectedEnabledValues(options, selectedValues);
	const selected = enabledOptions.length > 0 && selectedEnabledValues.length === enabledOptions.length;

	return {
		disabled: Boolean(group.disabled || enabledOptions.length === 0),
		group,
		indeterminate: selectedEnabledValues.length > 0 && !selected,
		label: group.label,
		options,
		selected,
		selectedValues: selectedEnabledValues,
		value: getGroupValue(group, index),
	};
};

export const useSelectGroups = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	groups,
	isMultiple = false,
	onChange,
	value,
}: IUseSelectGroupsProps<TValue, TMetadata>): IUseSelectGroupsResult<TValue, TMetadata> => {
	const selectedValues = React.useMemo(() => new Set(value), [value]);
	const { flatOptions, groupedOptions } = React.useMemo(() => {
		const nextGroupedOptions = groups.map((group, index) =>
			getGroupRenderModel({
				group,
				index,
				selectedValues,
			})
		);

		return {
			flatOptions: nextGroupedOptions.flatMap(group => group.options),
			groupedOptions: nextGroupedOptions,
		};
	}, [groups, selectedValues]);

	const toggleGroupSelection = React.useCallback(
		(group: ISelectGroupRenderModel<TValue, TMetadata>) => {
			if (!isMultiple || group.disabled) {
				return;
			}

			const enabledValues = getEnabledOptions(group.options).map(option => option.value);
			const enabledValueSet = new Set(enabledValues);
			const currentValueSet = new Set(value);
			const hasSelectedAllEnabledValues = enabledValues.every(enabledValue => currentValueSet.has(enabledValue));

			if (hasSelectedAllEnabledValues) {
				onChange(value.filter(selectedValue => !enabledValueSet.has(selectedValue)));
				return;
			}

			onChange([...value, ...enabledValues.filter(enabledValue => !currentValueSet.has(enabledValue))]);
		},
		[isMultiple, onChange, value]
	);

	return {
		flatOptions,
		groupedOptions,
		toggleGroupSelection,
	};
};
